import React from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ShieldCheck, Mic, ClipboardList, Stethoscope, ArrowRight, Lock, Brain, Sparkles, Timer, Languages, RotateCcw, Gauge } from "lucide-react";

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />

      {/* Hero */}
      <section className="mano-container py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-1 text-xs text-muted-foreground" data-testid="hero-badge">
            <Lock size={12} /> Consent-first · DPDP-aligned · Screening aid
          </span>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight" data-testid="hero-headline">
            Your Voice · <span className="text-primary">AI</span> · Your Health.
          </h1>
          <p className="text-base md:text-lg text-muted-foreground mt-6 leading-relaxed max-w-xl">
            Manodayam pairs your voice and standardised questionnaires with independent
            clinical review — a consent-first screening aid for mental health,
            neurodegenerative and substance-use concerns.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button
              size="lg"
              onClick={() => nav("/register")}
              className="rounded-full px-8 mano-lift"
              data-testid="cta-begin-assessment"
            >
              Begin assessment <ArrowRight size={16} />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => nav("/login")}
              className="rounded-full px-8"
              data-testid="cta-continue-assessment"
            >
              Continue where I left off
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-6 max-w-md">
            Not a diagnostic tool. Positioned as a screening aid — clinical action always requires a licensed practitioner.
          </p>
        </div>

        <div className="relative">
          <div
            className="aspect-[4/5] rounded-2xl overflow-hidden border border-border/70 shadow-sm bg-gradient-to-br from-primary/10 via-secondary/20 to-background"
            data-testid="hero-manaswini-frame"
          >
            <img
              src="https://customer-assets-agu9un31.emergentagent.net/job_consent-assess/artifacts/nu2lzmoj_Manaswini%201.jpg"
              alt="Manaswini — your guide through Manodayam"
              className="h-full w-full object-cover object-top"
              draggable="false"
              data-testid="hero-manaswini-image"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 w-64 rounded-xl border border-border/70 bg-card p-4 shadow-sm hidden md:block" data-testid="hero-manaswini-caption">
            <div className="text-xs text-muted-foreground">Meet</div>
            <div className="mt-1 text-sm font-medium">Manaswini · your assessment guide</div>
            <div className="mt-3 h-1.5 w-full rounded-full bg-muted">
              <div className="h-1.5 w-2/3 rounded-full bg-secondary" />
            </div>
          </div>
        </div>
      </section>

      {/* Voice-MoCA showcase — kept on the landing page as a Brain Health
          feature spotlight. Deliberately NOT surfaced inside the respondent
          questionnaire flow (per Feb-2026 product decision). */}
      <section className="mano-container py-16 border-t border-border/60" data-testid="moca-showcase">
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 via-secondary/10 to-transparent p-8 md:p-12">
          <div className="grid md:grid-cols-5 gap-10 items-center">
            <div className="md:col-span-3">
              <Badge className="border-primary/40 bg-primary/10 text-primary text-xs px-3 py-1"
                     variant="outline"
                     data-testid="moca-showcase-badge">
                <Sparkles size={12} /> Featured · Brain Health
              </Badge>
              <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.1] tracking-tight"
                  data-testid="moca-showcase-headline">
                Voice-driven MoCA — a 30-point cognitive screen you speak, not type.
              </h2>
              <p className="text-base md:text-lg text-muted-foreground mt-5 leading-relaxed max-w-2xl">
                Speak your answers to eighteen short tasks across seven cognitive domains.
                Whisper transcribes each recording, Claude scores it against a clinical
                rubric, and a licensed clinician reviews the result — all in about ten minutes.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                {[
                  { icon: Brain, title: "7 MoCA-inspired domains", body: "Executive · Naming · Attention · Language · Abstraction · Delayed recall · Orientation" },
                  { icon: Timer, title: "~10 minutes end-to-end", body: "Eighteen bite-sized voice tasks with auto-stop timers per prompt" },
                  { icon: Gauge, title: "Confidence per task", body: "The scorer flags anything under 70% so you can re-record and improve your score" },
                  { icon: RotateCcw, title: "Retake without restarting", body: "Any task can be re-recorded on the result page — totals refresh live" },
                  { icon: Languages, title: "Speak in your language", body: "Whisper auto-detects each clip, so you can codeswitch mid-assessment" },
                  { icon: Stethoscope, title: "Clinician-reviewable", body: "Every recording, transcript and rubric rationale is auditable end-to-end" },
                ].map((f, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <f.icon size={16} />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{f.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{f.body}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3 mt-8">
                <Button size="lg" onClick={() => nav("/moca-voice")}
                        className="rounded-full px-8 mano-lift"
                        data-testid="moca-showcase-cta-primary">
                  Try voice-MoCA <ArrowRight size={16} />
                </Button>
                <Button size="lg" variant="outline" onClick={() => nav("/register")}
                        className="rounded-full px-8"
                        data-testid="moca-showcase-cta-secondary">
                  Register for a full assessment
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Screening aid only — MoCA-inspired custom prompts. A clinician-administered
                MoCA is still required for diagnosis.
              </p>
            </div>

            <div className="md:col-span-2 space-y-4" aria-hidden>
              <Card className="mano-card" data-testid="moca-showcase-preview-recall">
                <CardContent className="p-5">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Task 1 of 18 · Encoding</div>
                  <div className="mt-2 text-sm text-foreground">Remember these five words — we'll ask you to recall them later.</div>
                  <div className="mt-3 rounded-lg border border-primary/40 bg-primary/5 p-3">
                    <div className="font-display text-xl tracking-wide text-primary">SHIP · ROSE · BREAD · ROBE · VIOLET</div>
                  </div>
                </CardContent>
              </Card>
              <Card className="mano-card" data-testid="moca-showcase-preview-naming">
                <CardContent className="p-5">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Task 7 of 18 · Naming</div>
                  <div className="mt-2 text-sm text-foreground">Name each animal aloud, left to right.</div>
                  <div className="mt-3 rounded-lg border border-primary/40 bg-primary/5 p-3 flex justify-around">
                    <span className="text-4xl" role="img" aria-label="elephant">🐘</span>
                    <span className="text-4xl" role="img" aria-label="kangaroo">🦘</span>
                    <span className="text-4xl" role="img" aria-label="giraffe">🦒</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="mano-card" data-testid="moca-showcase-preview-result">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">Score preview</div>
                      <div className="font-display text-3xl leading-none mt-1">27<span className="text-muted-foreground text-base"> / 30</span></div>
                    </div>
                    <Badge className="border-primary/30 bg-primary/10 text-primary" variant="outline">Normal</Badge>
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground">
                    Confidence per task · retake anything under 70% before you leave
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Four stages */}
      <section className="mano-container py-16 border-t border-border/60">
        <div className="max-w-2xl">
          <h2 className="font-display font-semibold text-2xl sm:text-3xl lg:text-4xl">A staged, transparent flow</h2>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            You are never surprised. Every stage tells you what it does, why, and who will see it.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-10" data-testid="staged-flow-grid">
          {[
            { icon: ShieldCheck, title: "Consent", body: "Versioned, purpose-specific consent in your preferred language. Withdraw any time." },
            { icon: Mic, title: "Voice sample", body: "Two short, affectively-neutral voice recordings — stored encrypted." },
            { icon: ClipboardList, title: "Questionnaires", body: "PHQ-9, GAD-7, PSS-10, MDQ, AD8, DAST-10, AUDIT — plus the voice-driven MoCA. One question at a time, auto-scored." },
            { icon: Stethoscope, title: "Clinical review", body: "Independent psychologist + psychiatrist review with blind reconciliation." },
          ].map((f, i) => (
            <Card key={i} className="mano-card mano-lift relative" data-testid={`staged-flow-card-${i + 1}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/40 text-primary">
                    <f.icon size={20} />
                  </div>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-primary font-display text-sm font-semibold"
                        data-testid={`staged-flow-step-${i + 1}`}
                        aria-label={`Step ${i + 1}`}>
                    {i + 1}
                  </span>
                </div>
                <div className="font-display font-semibold text-lg mt-4">
                  <span className="text-primary mr-1">{i + 1}.</span>{f.title}
                </div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Privacy strip */}
      <section className="mano-container py-16 border-t border-border/60">
        <div className="rounded-2xl border border-border/70 bg-card p-8 md:p-10 grid md:grid-cols-3 gap-8">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Encrypted</div>
            <div className="font-display text-xl mt-2">Voice + responses at rest</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Language-neutral</div>
            <div className="font-display text-xl mt-2">Scoring on item IDs, not text</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Auditable</div>
            <div className="font-display text-xl mt-2">Every consent action logged</div>
          </div>
        </div>
      </section>

      <footer className="mano-container py-12 text-sm text-muted-foreground">
        <div className="border-t border-border/60 pt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/manodayam-logo.png" alt="Manodayam"
                 className="h-6 w-auto opacity-80" draggable="false"
                 data-testid="footer-logo" />
            <span>© {new Date().getFullYear()} Manodayam</span>
          </div>
          <Link to="/doctor/login" className="hover:text-foreground transition-colors" data-testid="footer-doctor-link">
            Doctor Sign in
          </Link>
        </div>
      </footer>
    </div>
  );
}
